import { Component, Input, OnInit, OnDestroy, inject, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { FormArray, FormGroup, FormControl } from '@angular/forms';
import { ProductModel } from '@app/products/shared/product.model';
import { CurrencyPipe, NgClass } from '@angular/common';
import { Subject, takeUntil, debounceTime, switchMap, of, catchError, fromEvent } from 'rxjs';
import { EventProductItemService } from './event-product-item.service';
import { LucideAngularModule, ChevronUp, ChevronDown } from 'lucide-angular';

@Component({
  selector: 'app-product-item-pricing',
  template: `
    @if (shouldShowPricePreview) {
      <div #pricingPanel class="rounded-lg border-2 border-gray-300 dark:border-gray-700 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-lg"
           [ngClass]="{'shadow-[0_-12px_24px_-4px_rgba(0,0,0,0.35),0_-4px_8px_-2px_rgba(0,0,0,0.2)] border-gray-400 dark:border-gray-600': isFloating}">
        <!-- Header -->
        <div
          (click)="toggleExpanded()"
          class="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
          [attr.aria-label]="isExpanded ? 'Collapse' : 'Expand'"
          [attr.aria-expanded]="isExpanded"
          role="button"
          tabindex="0"
          (keydown.enter)="toggleExpanded()"
          (keydown.space)="toggleExpanded(); $event.preventDefault()">
          <h3 class="text-sm font-semibold text-gray-900 dark:text-white">Price Breakdown</h3>
          <lucide-icon [size]="18" [name]="isExpanded ? ChevronUp : ChevronDown" class="text-gray-600 dark:text-gray-400"></lucide-icon>
        </div>

        <!-- Content -->
        <div class="p-4 transition-all duration-300" [ngClass]="{'min-h-[200px]': isExpanded && variantBreakdown.length > 0, 'min-h-0': !isExpanded}">
          @if (!isExpanded) {
            <!-- Collapsed state: show only total -->
            @if (isCalculating) {
              <div class="animate-pulse">
                <div class="h-7 bg-gray-300 dark:bg-gray-600 rounded w-full"></div>
              </div>
            } @else if (calculationError) {
              <div class="text-sm text-red-500">Error calculating price</div>
            } @else {
              <div class="flex justify-between items-center">
                <span class="text-sm font-semibold text-gray-700 dark:text-gray-300">Total:</span>
                <span class="text-2xl font-bold"
                      [class.text-gray-900]="calculatedPrice !== null && calculatedPrice >= 0"
                      [class.dark:text-white]="calculatedPrice !== null && calculatedPrice >= 0"
                      [class.text-red-600]="calculatedPrice !== null && calculatedPrice < 0"
                      [class.dark:text-red-400]="calculatedPrice !== null && calculatedPrice < 0">
                  {{ calculatedPrice | currency:'GBP':'symbol':'1.2-2':'en-GB' }}
                </span>
              </div>
            }
          } @else if (isCalculating) {
            <!-- Loading Skeleton -->
            <div class="animate-pulse space-y-3">
              <!-- Base Price Skeleton -->
              <div class="flex justify-between gap-4">
                <div class="h-4 bg-gray-300 dark:bg-gray-600 rounded w-24"></div>
                <div class="h-4 bg-gray-300 dark:bg-gray-600 rounded w-20"></div>
              </div>

              <!-- Variant Lines Skeleton -->
              <div class="space-y-2">
                <div class="flex justify-between gap-4">
                  <div class="h-3 bg-gray-300 dark:bg-gray-600 rounded w-36"></div>
                  <div class="h-3 bg-gray-300 dark:bg-gray-600 rounded w-28"></div>
                </div>
                @if (variantSelectionsFormArray && variantSelectionsFormArray.length > 1) {
                  <div class="flex justify-between gap-4">
                    <div class="h-3 bg-gray-300 dark:bg-gray-600 rounded w-32"></div>
                    <div class="h-3 bg-gray-300 dark:bg-gray-600 rounded w-24"></div>
                  </div>
                }
              </div>

              <!-- Divider -->
              <div class="border-t border-gray-300 dark:border-gray-600 pt-3 mt-3">
                <div class="h-7 bg-gray-300 dark:bg-gray-600 rounded w-full"></div>
              </div>
            </div>
          } @else if (calculationError) {
            <div class="text-sm text-red-500 min-h-[120px] flex items-center justify-center">
              Error calculating price
            </div>
          } @else {
            <!-- Base Price -->
            @if (product.price !== null && product.price !== undefined) {
              <div class="text-sm text-gray-600 dark:text-gray-400" [ngClass]="{'mb-3': variantBreakdown.length > 0}">
                <div class="flex justify-between gap-4">
                  <span>Base Price:</span>
                  <span class="font-medium text-gray-900 dark:text-white">{{ product.price | currency:'GBP':'symbol':'1.2-2':'en-GB' }}</span>
                </div>
              </div>
            }

            <!-- Breakdown of variant selections -->
            @if (variantBreakdown.length > 0) {
              <div class="text-sm text-gray-600 dark:text-gray-400 space-y-2 mb-4">
                @for (item of variantBreakdown; track $index) {
                  <div class="space-y-1">
                    <div class="flex justify-between gap-4">
                      <span class="font-medium">{{ item.variantName }}:</span>
                      <span>{{ item.optionName }}</span>
                    </div>
                    <div class="flex justify-between gap-4 text-xs">
                      <span class="text-gray-500 dark:text-gray-500">{{ item.quantity }} × {{ item.unitPrice | currency:'GBP':'symbol':'1.2-2':'en-GB' }}</span>
                      <span class="font-medium text-gray-900 dark:text-white">{{ item.subtotal | currency:'GBP':'symbol':'1.2-2':'en-GB' }}</span>
                    </div>
                  </div>
                }
              </div>
            }

            <!-- Total -->
            <div class="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
              <div class="flex justify-between items-center">
                <span class="text-sm font-semibold text-gray-700 dark:text-gray-300">Total:</span>
                <span class="text-2xl font-bold"
                      [class.text-gray-900]="calculatedPrice !== null && calculatedPrice >= 0"
                      [class.dark:text-white]="calculatedPrice !== null && calculatedPrice >= 0"
                      [class.text-red-600]="calculatedPrice !== null && calculatedPrice < 0"
                      [class.dark:text-red-400]="calculatedPrice !== null && calculatedPrice < 0">
                  {{ calculatedPrice | currency:'GBP':'symbol':'1.2-2':'en-GB' }}
                </span>
              </div>
            </div>
          }
        </div>
      </div>
    }
  `,
  styles: [`
    :host {
      position: sticky;
      bottom: 1rem;
      z-index: 40;
      display: block;
      margin-top: 1rem;
    }
  `],
  imports: [CurrencyPipe, NgClass, LucideAngularModule],
  standalone: true,
})
export class ProductItemPricingComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() product!: ProductModel;
  @Input() eventId!: number;
  @Input() variantSelectionsFormArray!: FormArray;
  @Input() quantityControl!: FormControl<number | null>;
  @Input() amountControl?: FormControl<number | null>;
  @ViewChild('pricingPanel') pricingPanel!: ElementRef;

  private eventProductItemService = inject(EventProductItemService);
  private destroy$ = new Subject<void>();
  private calculatePrice$ = new Subject<void>();
  private observer?: IntersectionObserver;

  // Icon constants
  protected readonly ChevronUp = ChevronUp;
  protected readonly ChevronDown = ChevronDown;

  // Calculated values from backend
  calculatedPrice: number | null = null;
  isCalculating: boolean = false;
  calculationError: boolean = false;
  shouldShowPricePreview: boolean = false;
  isFloating: boolean = false;
  isExpanded: boolean = false; // Default to collapsed

  // Breakdown for displaying individual variant calculations
  variantBreakdown: Array<{
    variantName: string;
    optionName: string;
    quantity: number;
    unitPrice: number;
    subtotal: number;
  }> = [];

  ngOnInit(): void {
    // Set up debounced API call (500ms delay)
    this.calculatePrice$
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(500),
        switchMap(() => this.fetchPriceFromBackend()),
        catchError(error => {
          console.error('Error calculating price:', error);
          this.calculationError = true;
          this.isCalculating = false;
          return of(null);
        })
      )
      .subscribe(response => {
        this.isCalculating = false;
        if (response) {
          this.calculatedPrice = response.calculated_price;
          this.calculationError = false;
          // Always show price preview when we have a calculated price (including 0 and negative values)
          this.shouldShowPricePreview = this.calculatedPrice !== null;
          this.updateBreakdown();
        }
      });

    // Subscribe to form changes
    this.quantityControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.triggerCalculation());

    if (this.variantSelectionsFormArray) {
      this.variantSelectionsFormArray.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.triggerCalculation());

      this.variantSelectionsFormArray.statusChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.triggerCalculation());
    }

    if (this.amountControl) {
      this.amountControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.triggerCalculation());
    }

    // Initial calculation
    this.triggerCalculation();

    // Set up a small delay to catch any late form initialization
    setTimeout(() => {
      this.triggerCalculation();
    }, 100);
  }

  ngAfterViewInit(): void {
    // Use Intersection Observer to detect when panel is sticky/floating
    if (this.pricingPanel) {
      this.observer = new IntersectionObserver(
        ([entry]) => {
          // When intersectionRatio < 1, the element is partially out of view (sticky)
          this.isFloating = entry.intersectionRatio < 1;
        },
        { threshold: [1] }
      );

      this.observer.observe(this.pricingPanel.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.observer) {
      this.observer.disconnect();
    }
  }

  private triggerCalculation(): void {
    // Don't calculate if essential data is missing
    if (!this.product?.id || !this.eventId) {
      this.shouldShowPricePreview = false;
      return;
    }

    // Always show preview when we have a product (even with 0 quantity)
    // This ensures base price and calculated price are always visible
    this.shouldShowPricePreview = true; // Show container immediately to prevent jumping
    this.isCalculating = true;
    this.calculationError = false;
    this.calculatePrice$.next();
  }

  private fetchPriceFromBackend() {
    // Guard against null product ID
    if (!this.product?.id) {
      return of({ calculated_price: 0 });
    }

    const payload = {
      product_id: this.product.id,
      event_id: this.eventId,
      quantity: this.quantityControl.value || 0,
      amount: this.amountControl?.value || null,
      variant_selections: this.getVariantSelectionsPayload()
    };

    return this.eventProductItemService.calculatePrice(payload);
  }

  private getVariantSelectionsPayload(): Array<{
    variant_id: number;
    variant_option_id: number | null;
    quantity: number;
  }> | undefined {
    if (!this.variantSelectionsFormArray || this.variantSelectionsFormArray.length === 0) {
      return undefined;
    }

    return this.variantSelectionsFormArray.controls
      .map(control => control.value)
      .filter(selection => {
        return selection.variant_id && selection.quantity > 0;
      })
      .map(selection => ({
        variant_id: selection.variant_id,
        variant_option_id: selection.variant_option_id || null,
        quantity: selection.quantity || 0
      }));
  }

  private updateBreakdown(): void {
    this.variantBreakdown = [];

    if (!this.variantSelectionsFormArray || this.variantSelectionsFormArray.length === 0) {
      return;
    }

    for (const control of this.variantSelectionsFormArray.controls) {
      const selection = control.value;

      // Skip selections with no quantity or no option selected
      if (!selection.quantity || selection.quantity <= 0 || !selection.variant_option_id) {
        continue;
      }

      // Find the variant and option in the product model
      const variant = this.product.variants?.find(v => v.id === selection.variant_id);
      const option = variant?.options?.find(o => o.id === selection.variant_option_id);

      if (variant && option) {
        // Use effective_price if available (includes daily pricing), otherwise fall back to price
        const unitPrice = option['effective_price'] ?? option.price ?? 0;
        const subtotal = unitPrice * selection.quantity;

        this.variantBreakdown.push({
          variantName: variant.name,
          optionName: option.name,
          quantity: selection.quantity,
          unitPrice: unitPrice,
          subtotal: subtotal
        });
      }
    }
  }

  toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;
  }
}
